import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataEditor, { GridCellKind } from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowsRightLeftIcon,
  CheckIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { CheckIcon as SolidCheckIcon } from '@heroicons/react/24/solid';
import { supabase } from '../../../utils/supabaseClient';
import {
  DEFAULT_SETTINGS,
  PAY_RANGES,
  SEGMENT_LENGTH_FT,
  calculateNebraskaPayFactor,
  fmtNebraskaRange,
} from '../../../utils/pay/nebraskaPayCalculator';

const PAGE_SIZE = 1000;
const MAX_SELECTED_SESSIONS = 2;

const cn = (...classes) => classes.filter(Boolean).join(' ');

const fmtStation = (ft) => {
  if (ft == null || !Number.isFinite(Number(ft))) return '--';
  const value = Number(ft);
  const hundreds = Math.floor(value / 100);
  const remainder = Math.round(value % 100);
  return `${hundreds}+${String(remainder).padStart(2, '0')}`;
};

const fmtCurrency = (v) => {
  const formatted = Math.abs(v || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `-${formatted}` : formatted;
};

const fmtExcelNumber = (value, digits = 2) => {
  if (!Number.isFinite(value)) return '';
  return value.toFixed(digits);
};

const getSessionLabel = (session) => session?.name || session?.filename || 'Unnamed Session';

const getSessionDate = (session) => session?.run_date || session?.created_at || session?.upload_timestamp;

const fmtDate = (dateString) => {
  if (!dateString) return 'No date';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getSessionRange = (session) => {
  const start = session?.analysis_start_station ?? session?.start_station_ft;
  const end = session?.analysis_end_station ?? session?.end_station_ft;
  if (start == null || end == null) return '--';
  return `${fmtStation(Math.min(start, end))} - ${fmtStation(Math.max(start, end))}`;
};

const getPayFactorClass = (payFactor) => {
  if (payFactor >= 1.03) return 'bg-green-100 text-green-700';
  if (payFactor > 1.00) return 'bg-lime-100 text-lime-700';
  if (payFactor === 1.00) return 'bg-blue-100 text-blue-700';
  if (payFactor >= 0.96) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
};

const SummaryCard = ({ label, value, unit, valueClassName = '' }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className={cn('text-xl font-bold text-gray-900', valueClassName)}>
      {value}
      {unit && <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>}
    </div>
  </div>
);

const PayTableModal = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} className="relative z-50">
    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
    <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
      <DialogPanel className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
          <DialogTitle className="text-base font-semibold text-gray-900">
            Nebraska Smoothness Pay Factor Table
          </DialogTitle>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <table className="w-full text-sm border border-gray-200">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left border-b">Average IRI (in/mi)</th>
                <th className="px-3 py-2 text-left border-b">Pay Factor</th>
                <th className="px-3 py-2 text-left border-b">Deduction</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {PAY_RANGES.map(row => (
                <tr key={row.code} className={row.deductionRate > 0 ? 'bg-red-50' : ''}>
                  <td className="px-3 py-2 font-semibold text-gray-900">{fmtNebraskaRange(row)}</td>
                  <td className="px-3 py-2">
                    <span className={cn('inline-block px-2 py-0.5 rounded-sm text-xs font-bold', getPayFactorClass(row.payFactor))}>
                      {row.payFactor.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-gray-700">
                    {row.deductionRate > 0 ? `${fmtCurrency(row.deductionRate)} per ${SEGMENT_LENGTH_FT} ft` : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="p-3 bg-gray-50 rounded-sm text-xs text-gray-600 space-y-1">
            <p className="font-semibold">Reference</p>
            <p>Overall pay factor is length-weighted across non-exception segments. Deduction is separate from the pay factor.</p>
          </div>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
);

const SessionSelectionModal = ({
  isOpen,
  onClose,
  sessions = [],
  folders = [],
  selectedSessionIds = [],
  onApply,
}) => {
  const [draftSelection, setDraftSelection] = useState(selectedSessionIds);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [currentFolderId, setCurrentFolderId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setDraftSelection(selectedSessionIds);
    setSearchQuery('');
    setCurrentFolderId(null);
  }, [isOpen, selectedSessionIds]);

  const currentFolder = useMemo(
    () => folders.find(folder => folder.id === currentFolderId),
    [folders, currentFolderId]
  );

  const folderPath = useMemo(() => {
    if (!currentFolderId) return [];
    const path = [];
    let current = currentFolder;
    while (current) {
      path.unshift(current);
      current = folders.find(folder => folder.id === current.parent_folder_id);
    }
    return path;
  }, [currentFolderId, currentFolder, folders]);

  const visibleFolders = useMemo(() => {
    if (searchQuery.trim()) return [];
    return folders
      .filter(folder => currentFolderId ? folder.parent_folder_id === currentFolderId : !folder.parent_folder_id)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [folders, currentFolderId, searchQuery]);

  const visibleSessions = useMemo(() => {
    let result = sessions;

    if (!searchQuery.trim()) {
      result = result.filter(session => currentFolderId ? session.folder_id === currentFolderId : !session.folder_id);
    } else {
      const query = searchQuery.toLowerCase();
      result = sessions.filter(session => {
        const name = getSessionLabel(session).toLowerCase();
        const operator = (session.operator || '').toLowerCase();
        const date = fmtDate(getSessionDate(session)).toLowerCase();
        const lane = String(session.lane_no || '').toLowerCase();
        return name.includes(query) || operator.includes(query) || date.includes(query) || lane.includes(query);
      });
    }

    return [...result].sort((a, b) => {
      if (sortBy === 'date_asc') return new Date(getSessionDate(a) || 0) - new Date(getSessionDate(b) || 0);
      if (sortBy === 'name') return getSessionLabel(a).localeCompare(getSessionLabel(b));
      if (sortBy === 'iri') return (a.avg_mri || 0) - (b.avg_mri || 0);
      return new Date(getSessionDate(b) || 0) - new Date(getSessionDate(a) || 0);
    });
  }, [sessions, currentFolderId, searchQuery, sortBy]);

  const selectedSessionsOrdered = useMemo(() => (
    draftSelection
      .map(id => sessions.find(session => session.session_id === id))
      .filter(Boolean)
      .sort((a, b) => new Date(getSessionDate(a) || 0) - new Date(getSessionDate(b) || 0))
  ), [draftSelection, sessions]);

  const getSessionFolder = (session) => {
    if (!session.folder_id) return null;
    return folders.find(folder => folder.id === session.folder_id);
  };

  const toggleSession = (sessionId) => {
    if (draftSelection.includes(sessionId)) {
      setDraftSelection(prev => prev.filter(id => id !== sessionId));
    } else if (draftSelection.length < MAX_SELECTED_SESSIONS) {
      setDraftSelection(prev => [...prev, sessionId]);
    }
  };

  const applySelection = () => {
    if (draftSelection.length === 0) return;
    onApply(draftSelection);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-sm shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Select Sessions for Pay Analysis</h2>
              <p className="text-sm text-gray-500 mt-0.5">Choose 1 or 2 completed sessions</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-sm transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 flex min-h-0 overflow-hidden">
            <div className="w-2/3 border-r border-gray-200 flex flex-col min-w-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-1 text-sm mb-3">
                  {currentFolderId && (
                    <button
                      onClick={() => setCurrentFolderId(currentFolder?.parent_folder_id || null)}
                      className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-sm transition-colors mr-1"
                    >
                      <ArrowLeftIcon className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setCurrentFolderId(null)}
                    className={cn(
                      'px-2 py-1 rounded-sm hover:bg-gray-200 transition-colors',
                      !currentFolderId ? 'font-medium text-gray-900 bg-white shadow-sm' : 'text-gray-600'
                    )}
                  >
                    Root
                  </button>
                  {folderPath.map(folder => (
                    <React.Fragment key={folder.id}>
                      <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <button
                        onClick={() => setCurrentFolderId(folder.id)}
                        className={cn(
                          'px-2 py-1 rounded-sm hover:bg-gray-200 transition-colors truncate max-w-[150px]',
                          folder.id === currentFolderId ? 'font-medium text-gray-900 bg-white shadow-sm' : 'text-gray-600'
                        )}
                        title={folder.name}
                      >
                        {folder.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>

                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search all sessions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="text-sm border border-gray-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                  >
                    <option value="date_desc">Newest First</option>
                    <option value="date_asc">Oldest First</option>
                    <option value="name">Name (A-Z)</option>
                    <option value="iri">IRI (Low to High)</option>
                  </select>
                </div>
              </div>

              <div className="flex-1 overflow-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="w-12 px-4 py-3 text-left"></th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Lane</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Distance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Run Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {visibleFolders.map(folder => {
                      const folderSessionCount = sessions.filter(session => session.folder_id === folder.id).length;
                      const subfolderCount = folders.filter(item => item.parent_folder_id === folder.id).length;

                      return (
                        <tr
                          key={`folder-${folder.id}`}
                          onClick={() => setCurrentFolderId(folder.id)}
                          className="hover:bg-yellow-50 cursor-pointer group"
                        >
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <FolderIcon className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate group-hover:text-yellow-700">
                                  {folder.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {subfolderCount > 0 && `${subfolderCount} folder${subfolderCount !== 1 ? 's' : ''}, `}
                                  {folderSessionCount} session{folderSessionCount !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3">
                            <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                          </td>
                        </tr>
                      );
                    })}

                    {visibleSessions.map(session => {
                      const isSelected = draftSelection.includes(session.session_id);
                      const canSelect = draftSelection.length < MAX_SELECTED_SESSIONS || isSelected;
                      const sessionFolder = searchQuery ? getSessionFolder(session) : null;

                      return (
                        <tr
                          key={session.session_id}
                          onClick={() => canSelect && toggleSession(session.session_id)}
                          className={cn(
                            'transition-colors',
                            isSelected && 'bg-red-50 hover:bg-red-100',
                            !isSelected && canSelect && 'hover:bg-gray-50 cursor-pointer',
                            !canSelect && 'opacity-50 cursor-not-allowed'
                          )}
                        >
                          <td className="px-4 py-3">
                            <div
                              className={cn(
                                'w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors',
                                isSelected ? 'bg-red-600 border-red-600' : 'border-gray-300'
                              )}
                            >
                              {isSelected && <SolidCheckIcon className="w-3 h-3 text-white" />}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <DocumentIcon className={cn('w-5 h-5 flex-shrink-0', isSelected ? 'text-red-500' : 'text-gray-400')} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate" title={getSessionLabel(session)}>
                                  {getSessionLabel(session)}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  {session.operator && <span>{session.operator}</span>}
                                  {sessionFolder && (
                                    <span className="flex items-center gap-1 text-yellow-600">
                                      <FolderIcon className="w-3 h-3" />
                                      {sessionFolder.name}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{session.lane_no || '--'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {session.total_distance_mi ? `${session.total_distance_mi.toFixed(2)} mi` : '--'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{fmtDate(getSessionDate(session))}</td>
                        </tr>
                      );
                    })}

                    {visibleFolders.length === 0 && visibleSessions.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-4 py-16 text-center">
                          <FolderIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm font-medium text-gray-900">
                            {searchQuery ? 'No sessions match your search' : 'No completed sessions in this folder'}
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="w-1/3 flex flex-col bg-gray-50 min-w-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Selected <span className="text-red-600">({draftSelection.length}/{MAX_SELECTED_SESSIONS})</span>
                  </h3>
                  {draftSelection.length > 0 && (
                    <button
                      onClick={() => setDraftSelection([])}
                      className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 min-h-0">
                {selectedSessionsOrdered.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-gray-400">
                      <ArrowsRightLeftIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm font-medium">No sessions selected</p>
                      <p className="text-xs mt-1">Click a session to add it</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedSessionsOrdered.map((session, index) => (
                      <div key={session.session_id} className="bg-white rounded-sm border border-gray-200 p-3 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate" title={getSessionLabel(session)}>
                              {getSessionLabel(session)}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">{fmtDate(getSessionDate(session))}</p>
                            <p className="text-xs text-gray-400">{getSessionRange(session)}</p>
                            <p className="text-xs text-gray-400">{session.lane_no ? `Lane ${session.lane_no}` : 'No lane'}</p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDraftSelection(prev => prev.filter(id => id !== session.session_id));
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors flex-shrink-0"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <p className="text-sm text-gray-500">
              {draftSelection.length === 0 && 'Select at least 1 session'}
              {draftSelection.length === 1 && '1 session ready'}
              {draftSelection.length === 2 && '2 sessions ready'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={applySelection}
                disabled={draftSelection.length < 1}
                className="px-5 py-2 text-sm bg-gray-800 text-white rounded-sm hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Apply Sessions
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const fetchAllSegmentsForSession = async (session) => {
  const rows = [];
  let from = 0;

  while (true) {
    const to = from + PAGE_SIZE - 1;
    const { data, error } = await supabase
      .from('prototype_segments')
      .select('*')
      .eq('session_id', session.session_id)
      .order('from_station_ft', { ascending: true })
      .range(from, to);

    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return rows.map(segment => ({
    ...segment,
    sessionInfo: {
      session_id: session.session_id,
      name: session.name,
      filename: session.filename,
      direction: session.direction,
      lane_no: session.lane_no,
      run_date: session.run_date,
    },
  }));
};

const combineSessionPayData = (selectedSessions, segmentsBySession) => {
  const sessionResults = selectedSessions
    .map(session => calculateNebraskaPayFactor(
      segmentsBySession[session.session_id] || [],
      DEFAULT_SETTINGS,
      false
    ))
    .filter(Boolean);

  if (sessionResults.length === 0) return null;

  const rangeSummary = PAY_RANGES.map(row => ({
    ...row,
    totalLengthFt: 0,
    weightedLength: 0,
    deductionDollars: 0,
    segmentCount: 0,
  }));

  const rows = [];
  let totalWeightedLength = 0;
  let totalLengthFt = 0;
  let totalDeductionDollars = 0;
  let iriLengthSum = 0;
  let deductionSegmentCount = 0;
  let lengthMismatchCount = 0;

  for (const result of sessionResults) {
    for (const row of result.rows) {
      rows.push({ ...row, num: rows.length + 1 });
    }

    for (const resultRange of result.rangeSummary) {
      const summary = rangeSummary.find(item => item.code === resultRange.code);
      if (!summary) continue;
      summary.totalLengthFt += resultRange.totalLengthFt;
      summary.weightedLength += resultRange.weightedLength;
      summary.deductionDollars += resultRange.deductionDollars;
      summary.segmentCount += resultRange.segmentCount;
    }

    totalWeightedLength += result.totalWeightedLength;
    totalLengthFt += result.totalLengthFt;
    totalDeductionDollars += result.totalDeductionDollars;
    iriLengthSum += result.avgIri * result.totalLengthFt;
    deductionSegmentCount += result.deductionSegmentCount;
    lengthMismatchCount += result.lengthMismatchCount;
  }

  return {
    rows,
    rangeSummary,
    overallPayFactor: totalLengthFt > 0 ? totalWeightedLength / totalLengthFt : 0,
    avgIri: totalLengthFt > 0 ? iriLengthSum / totalLengthFt : 0,
    totalLengthFt,
    totalWeightedLength,
    totalDeductionDollars,
    segmentCount: rows.length,
    deductionSegmentCount,
    lengthMismatchCount,
  };
};

export default function NebraskaProjectPay({ project, sessions = [], folders = [] }) {
  const completedSessions = useMemo(
    () => sessions.filter(session => session.status === 'COMPLETED'),
    [sessions]
  );
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);
  const [segmentsBySession, setSegmentsBySession] = useState({});
  const [activeTab, setActiveTab] = useState('combined');
  const [modalOpen, setModalOpen] = useState(true);
  const [showPayTable, setShowPayTable] = useState(false);
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [copiedExcelRows, setCopiedExcelRows] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedSessions = useMemo(() => {
    const selected = new Set(selectedSessionIds);
    return completedSessions
      .filter(session => selected.has(session.session_id))
      .sort((a, b) => new Date(getSessionDate(a) || 0) - new Date(getSessionDate(b) || 0));
  }, [completedSessions, selectedSessionIds]);

  useEffect(() => {
    if (selectedSessionIds.length === 0) {
      setModalOpen(true);
    }
  }, [selectedSessionIds.length]);

  useEffect(() => {
    if (activeTab !== 'combined' && !selectedSessionIds.includes(activeTab)) {
      setActiveTab('combined');
    }
  }, [activeTab, selectedSessionIds]);

  useEffect(() => {
    setCopiedExcelRows(false);
  }, [activeTab, selectedSessionIds]);

  useEffect(() => {
    let cancelled = false;

    const loadSegments = async () => {
      setError('');
      if (selectedSessions.length === 0) {
        setSegmentsBySession({});
        return;
      }

      setLoading(true);
      try {
        const entries = await Promise.all(
          selectedSessions.map(async (session) => [
            session.session_id,
            await fetchAllSegmentsForSession(session),
          ])
        );

        if (!cancelled) {
          setSegmentsBySession(Object.fromEntries(entries));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load Nebraska project pay segments:', err);
          setError(err.message || 'Failed to load segment data.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSegments();
    return () => {
      cancelled = true;
    };
  }, [selectedSessions]);

  const tabOptions = useMemo(() => [
    { id: 'combined', label: 'Combined' },
    ...selectedSessions.map((session, index) => ({
      id: session.session_id,
      label: selectedSessions.length === 1 ? getSessionLabel(session) : `Session ${index + 1}`,
      title: getSessionLabel(session),
    })),
  ], [selectedSessions]);

  const activeSegments = useMemo(() => {
    if (activeTab === 'combined') {
      return selectedSessions.flatMap(session => segmentsBySession[session.session_id] || []);
    }
    return segmentsBySession[activeTab] || [];
  }, [activeTab, selectedSessions, segmentsBySession]);

  const data = useMemo(() => {
    if (activeTab === 'combined') {
      return combineSessionPayData(selectedSessions, segmentsBySession);
    }
    return calculateNebraskaPayFactor(activeSegments, DEFAULT_SETTINGS, false);
  }, [activeTab, activeSegments, selectedSessions, segmentsBySession]);

  const displayRows = useMemo(() => {
    if (!data) return [];
    return issuesOnly ? data.rows.filter(row => row.hasIssue) : data.rows;
  }, [data, issuesOnly]);

  const segmentGridColumns = useMemo(() => {
    const sessionColumn = activeTab === 'combined'
      ? [{ id: 'session', title: 'Session', width: 150 }]
      : [];
    const dataColumns = [
      { id: 'num', title: '#', width: 70 },
      ...sessionColumn,
      { id: 'startStation', title: 'Beginning Sta.', width: 140 },
      { id: 'stopStation', title: 'Ending Sta.', width: 140 },
      { id: 'lengthFt', title: 'Length (ft)', width: 120 },
      { id: 'avgIri', title: 'Avg IRI', width: 110 },
      { id: 'rangeLabel', title: 'IRI Range', width: 115 },
      { id: 'payFactor', title: 'Pay Factor', width: 115 },
      { id: 'deduction', title: 'Deduction', width: 130 },
    ];
    const fillerColumns = Array.from({ length: 8 }, (_, index) => ({
      id: `blank-${index + 1}`,
      title: '',
      width: 110,
      grow: 1,
    }));

    return [...dataColumns, ...fillerColumns];
  }, [activeTab]);

  const segmentGridRows = useMemo(() => {
    const rows = [];
    let lastSessionId = null;
    let sessionBlockNumber = 1;

    displayRows.forEach((row) => {
      const sessionId = row.sessionId || row.sessionInfo?.session_id || '';
      if (activeTab === 'combined' && !issuesOnly && lastSessionId && sessionId !== lastSessionId) {
        sessionBlockNumber += 1;
        rows.push({
          num: '',
          session: `Session ${sessionBlockNumber}: ${getSessionLabel(row.sessionInfo)}`,
          startStation: '',
          stopStation: '',
          lengthFt: '',
          avgIri: '',
          rangeLabel: '',
          payFactor: '',
          deduction: '',
          isSessionDivider: true,
        });
      }

      rows.push({
        num: String(row.num),
        session: getSessionLabel(row.sessionInfo),
        startStation: fmtStation(row.startStation),
        stopStation: fmtStation(row.stopStation),
        lengthFt: row.lengthFt.toFixed(0),
        avgIri: row.avgIri.toFixed(1),
        rangeLabel: row.rangeLabel,
        payFactor: row.payFactor.toFixed(2),
        deduction: row.deductionDollars > 0 ? fmtCurrency(row.deductionDollars) : '',
        hasIssue: row.hasIssue,
      });
      lastSessionId = sessionId;
    });

    return rows;
  }, [activeTab, displayRows, issuesOnly]);

  const totalGridRow = useMemo(() => data ? {
    num: '',
    session: '',
    startStation: '',
    stopStation: 'Total:',
    lengthFt: data.totalLengthFt.toFixed(0),
    avgIri: `Avg: ${data.avgIri.toFixed(1)}`,
    rangeLabel: '',
    payFactor: data.overallPayFactor.toFixed(4),
    deduction: data.totalDeductionDollars > 0 ? fmtCurrency(data.totalDeductionDollars) : '',
    hasIssue: data.totalDeductionDollars > 0,
    isTotal: true,
  } : null, [data]);

  const gridRowsWithTotal = useMemo(() => (
    totalGridRow ? [...segmentGridRows, totalGridRow] : segmentGridRows
  ), [segmentGridRows, totalGridRow]);

  const getSegmentCellContent = useCallback(([col, row]) => {
    const gridRow = gridRowsWithTotal[row];
    const column = segmentGridColumns[col];
    const value = gridRow?.[column?.id] ?? '';

    let themeOverride;
    if (gridRow?.isTotal) {
      themeOverride = {
        bgCell: '#f3f4f6',
        bgCellMedium: '#e5e7eb',
        textDark: gridRow.hasIssue && column?.id === 'deduction' ? '#b91c1c' : '#111827',
      };
    } else if (gridRow?.hasIssue) {
      themeOverride = {
        bgCell: '#fef2f2',
        bgCellMedium: '#fee2e2',
        textDark: column?.id === 'avgIri' || column?.id === 'deduction' ? '#b91c1c' : '#111827',
      };
    } else if (gridRow?.isSessionDivider) {
      themeOverride = {
        bgCell: '#f3f4f6',
        bgCellMedium: '#e5e7eb',
        textDark: '#374151',
      };
    }

    return {
      kind: GridCellKind.Text,
      data: String(value),
      displayData: String(value),
      allowOverlay: false,
      readonly: true,
      themeOverride,
    };
  }, [gridRowsWithTotal, segmentGridColumns]);

  const activeTabLabel = useMemo(() => {
    if (activeTab === 'combined') return 'Combined';
    const session = selectedSessions.find(item => item.session_id === activeTab);
    return getSessionLabel(session);
  }, [activeTab, selectedSessions]);

  const canCopyExcelRows = activeTab !== 'combined' && !!data?.rows?.length;

  const warnings = useMemo(() => {
    const messages = [];
    if (sessions.some(session => session.status !== 'COMPLETED')) messages.push('Processing or failed sessions are excluded from selection.');
    if (selectedSessions.length > 0 && activeSegments.length === 0 && !loading) messages.push('Selected sessions have no segment data.');
    return messages;
  }, [selectedSessions, sessions, activeSegments.length, loading]);

  const handleApplySessions = useCallback((ids) => {
    setSelectedSessionIds(ids.slice(0, MAX_SELECTED_SESSIONS));
    setActiveTab('combined');
    setIssuesOnly(false);
  }, []);

  const copyExcelRows = async () => {
    if (!canCopyExcelRows) return;

    const tsv = data.rows.map(row => [
      fmtExcelNumber(row.startStation, 0),
      fmtExcelNumber(row.stopStation, 0),
      fmtExcelNumber(row.lengthFt, 2),
      fmtExcelNumber(row.leftIri, 2),
      fmtExcelNumber(row.rightIri, 2),
    ].join('\t')).join('\n');

    try {
      await navigator.clipboard.writeText(tsv);
      setCopiedExcelRows(true);
      setTimeout(() => setCopiedExcelRows(false), 1500);
    } catch (err) {
      console.error('Failed to copy segment rows:', err);
    }
  };

  const exportCSV = () => {
    if (!displayRows.length) return;

    const headers = [
      ...(activeTab === 'combined' ? ['Session'] : []),
      'Beginning Sta.',
      'Ending Sta.',
      'Length (ft)',
      'Left IRI',
      'Right IRI',
      'Average IRI',
      'IRI Range',
      'Pay Factor',
      'Deduction',
    ].join(',');

    const csvRows = displayRows.map(row => [
      ...(activeTab === 'combined' ? [getSessionLabel(row.sessionInfo)] : []),
      row.startStation.toFixed(0),
      row.stopStation.toFixed(0),
      row.lengthFt.toFixed(2),
      fmtExcelNumber(row.leftIri, 2),
      fmtExcelNumber(row.rightIri, 2),
      row.avgIri.toFixed(2),
      row.rangeLabel,
      row.payFactor.toFixed(4),
      row.deductionDollars.toFixed(2),
    ].map(cell => {
      const value = String(cell ?? '');
      return value.includes(',') || value.includes('"') ? `"${value.replace(/"/g, '""')}"` : value;
    }).join(','));

    const blob = new Blob([[headers, ...csvRows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `nebraska-project-pay-${activeTabLabel.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="bg-white h-[calc(100vh-56px)] flex flex-col">
      <PayTableModal open={showPayTable} onClose={() => setShowPayTable(false)} />
      <SessionSelectionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        sessions={completedSessions}
        folders={folders}
        selectedSessionIds={selectedSessionIds}
        onApply={handleApplySessions}
      />

      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3 bg-gray-50">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Nebraska Smoothness Pay Factor</h3>
          <p className="text-xs text-gray-500">Project pay analysis for {project?.name || 'this project'}</p>
        </div>

        <div className="flex-1" />

        {data?.deductionSegmentCount > 0 && (
          <div className="h-8 px-2.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-sm flex items-center gap-1.5 whitespace-nowrap">
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
            {data.deductionSegmentCount} corrective deduction{data.deductionSegmentCount > 1 ? 's' : ''}
          </div>
        )}

        <button
          onClick={() => setModalOpen(true)}
          className="h-8 px-3 text-sm text-white bg-red-600 border border-red-600 rounded-sm hover:bg-red-700 flex items-center gap-1.5"
        >
          <PlusIcon className="h-4 w-4" />
          Select Sessions
          <span className="bg-red-700 px-1.5 py-0.5 rounded text-xs">{selectedSessionIds.length}/{MAX_SELECTED_SESSIONS}</span>
        </button>

        <button
          onClick={() => setShowPayTable(true)}
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 flex items-center gap-1.5"
        >
          <InformationCircleIcon className="h-4 w-4" />
          Pay Table
        </button>

        <button
          onClick={copyExcelRows}
          disabled={!canCopyExcelRows}
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          {copiedExcelRows ? (
            <CheckIcon className="h-4 w-4 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-4 w-4" />
          )}
          {activeTab === 'combined' ? 'Session Tabs Only' : copiedExcelRows ? 'Copied' : 'Copy Excel Rows'}
        </button>

        <button
          onClick={exportCSV}
          disabled={!displayRows.length}
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export
        </button>
      </div>

      {selectedSessionIds.length > 0 && (
        <div className="px-4 py-2 bg-white border-b border-gray-200 flex items-center gap-2 overflow-x-auto">
          {tabOptions.map(tab => (
            <button
              key={tab.id}
              title={tab.title || tab.label}
              onClick={() => {
                setActiveTab(tab.id);
                setIssuesOnly(false);
              }}
              className={cn(
                'h-8 px-3 text-sm font-medium rounded-sm border whitespace-nowrap',
                activeTab === tab.id
                  ? 'bg-gray-900 border-gray-900 text-white'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {warnings.length > 0 && (
        <div className="px-4 py-2 border-b border-yellow-200 bg-yellow-50 space-y-1">
          {warnings.map(message => (
            <div key={message} className="flex items-center gap-2 text-sm text-yellow-800">
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
              {message}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="px-4 py-2 border-b border-red-200 bg-red-50 flex items-center gap-2 text-sm text-red-700">
          <XCircleIcon className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading && (
        <div className="px-4 py-2 border-b border-blue-200 bg-blue-50 flex items-center gap-2 text-sm text-blue-700">
          <div className="h-4 w-4 rounded-full border-2 border-blue-300 border-t-blue-700 animate-spin" />
          Loading selected session segments...
        </div>
      )}

      {selectedSessionIds.length === 0 ? (
        <div className="px-6 py-16">
          <div className="text-center max-w-sm mx-auto">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <ArrowsRightLeftIcon className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-900">No sessions selected</h3>
            <p className="text-xs text-gray-500 mt-1">Select one or two completed sessions to calculate Nebraska project pay.</p>
            <button
              onClick={() => setModalOpen(true)}
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Select Sessions
            </button>
          </div>
        </div>
      ) : data ? (
        <>
          <div className="px-4 py-4 border-b border-gray-200 space-y-3">
            <div className="grid grid-cols-5 gap-3">
              <SummaryCard
                label="Pay Factor"
                value={data.overallPayFactor.toFixed(4)}
                valueClassName={cn(
                  data.overallPayFactor > 1 && 'text-green-700',
                  data.overallPayFactor < 1 && 'text-red-700'
                )}
              />
              <SummaryCard label="Pay Percent" value={`${(data.overallPayFactor * 100).toFixed(2)}%`} />
              <SummaryCard
                label="Deduction"
                value={fmtCurrency(data.totalDeductionDollars)}
                valueClassName={data.totalDeductionDollars > 0 ? 'text-red-700' : ''}
              />
              <SummaryCard label="Avg IRI" value={data.avgIri.toFixed(1)} unit="in/mi" />
              <SummaryCard label="Total Length" value={data.totalLengthFt.toFixed(0)} unit="ft" />
            </div>

            {data.lengthMismatchCount > 0 && (
              <div className="flex items-center gap-2 text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 px-3 py-2 rounded-sm">
                <ExclamationTriangleIcon className="h-4 w-4" />
                {data.lengthMismatchCount} segment{data.lengthMismatchCount > 1 ? 's' : ''} use an explicit length that differs from station distance.
              </div>
            )}

          </div>

          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {activeTabLabel} Segments <span className="text-gray-400 font-normal">({data.rows.length})</span>
            </span>
            {data.deductionSegmentCount > 0 && (
              <label className="flex items-center gap-1.5 text-gray-500 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={issuesOnly}
                  onChange={e => setIssuesOnly(e.target.checked)}
                  className="w-3.5 h-3.5 text-blue-600 rounded-sm"
                />
                Deductions only ({data.deductionSegmentCount})
              </label>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            {displayRows.length > 0 ? (
              <DataEditor
                columns={segmentGridColumns}
                rows={gridRowsWithTotal.length}
                getCellContent={getSegmentCellContent}
                getCellsForSelection={true}
                rowMarkers="none"
                rowHeight={34}
                headerHeight={38}
                smoothScrollX={true}
                smoothScrollY={true}
                rangeSelect="multi-rect"
                freezeColumns={1}
                theme={{
                  accentColor: '#2563eb',
                  accentLight: 'rgba(37, 99, 235, 0.12)',
                  fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
                  cellHorizontalPadding: 12,
                  headerFontStyle: '600 12px',
                  baseFontStyle: '13px',
                }}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400 py-12">
                <div className="text-center">
                  <XCircleIcon className="h-10 w-10 mx-auto mb-2" />
                  <p>{issuesOnly ? 'No segments with deductions' : 'No segment data available'}</p>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="px-4 py-8 text-center text-gray-400 border-b border-gray-200">
          No segment data available
        </div>
      )}

      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
        <span>Nebraska AC Pavement Smoothness Pay Factor</span>
        <span>Segment basis: {SEGMENT_LENGTH_FT} ft</span>
      </div>
    </div>
  );
}
